import { useRouteError } from 'react-router-dom'

import type { ReactElement } from 'react'

import { Button } from '@/components/ui/button'

const RELOAD_FLAG = 'vanta:chunk-reload'

/**
 * Erro de chunk que sumiu do servidor. Acontece com a aba aberta durante um deploy: o
 * `index.html` que o navegador carregou aponta para arquivos com hash da versão anterior, que
 * não existem mais. Não é bug de código — é versão velha pedindo peça velha.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  )
}

export function RouteErrorBoundary(): ReactElement | null {
  const error = useRouteError()

  if (isStaleChunkError(error)) {
    // Recarrega UMA vez por sessão. Sem a trava, um chunk que falha por outro motivo (rede caindo,
    // proxy bloqueando) viraria laço infinito de reload — pior que a tela de erro.
    if (sessionStorage.getItem(RELOAD_FLAG) === null) {
      sessionStorage.setItem(RELOAD_FLAG, '1')
      window.location.reload()
      return null
    }
  }

  return (
    <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold">Algo deu errado</h1>
        <p className="text-sm text-muted-foreground">
          Recarregue a página. Se continuar, fale com o suporte.
        </p>
      </div>
      <Button onClick={() => window.location.reload()}>Recarregar</Button>
    </div>
  )
}
